import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { authService } from '../lib/database/auth';
import { supabase } from '../lib/supabase';
import { Database } from '../types/database';

type Profile = Database['public']['Tables']['profiles']['Row'];

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('useAuth: Starting auth check');
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('useAuth: Getting initial session');
        const { user: currentUser, profile: currentProfile } = await authService.getCurrentUser();
        console.log('useAuth: Initial session result', { 
          user: currentUser?.id, 
          profile: currentProfile?.role,
          email: currentProfile?.email
        });
        
        setUser(currentUser);
        setProfile(currentProfile);
        
        // Always set loading to false after initial check
        setLoading(false);
      } catch (error) {
        console.error('useAuth: Error getting initial session:', error);
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes using supabase client directly
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('useAuth: Auth state changed:', event, session?.user?.id);
      
      if (session?.user) {
        try {
          // Wait a bit for the profile to be created by the trigger
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { user: currentUser, profile: currentProfile } = await authService.getCurrentUser();
          console.log('useAuth: Auth change result', { 
            user: currentUser?.id, 
            profile: currentProfile?.role,
            email: currentProfile?.email
          });
          setUser(currentUser);
          setProfile(currentProfile);
        } catch (error) {
          console.error('useAuth: Error fetching user profile:', error);
          setUser(session.user);
          setProfile(null);
        }
      } else {
        console.log('useAuth: No session, clearing user');
        setUser(null);
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      console.log('useAuth: Signing in user:', email);
      const { data, error } = await authService.signIn(email, password);
      
      if (error) {
        console.error('useAuth: Sign in error:', error);
        setLoading(false);
        return { user: null, error };
      }

      if (data.user) {
        // Wait for profile to be available
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { user: currentUser, profile: currentProfile } = await authService.getCurrentUser();
        console.log('useAuth: Sign in success', { 
          user: currentUser?.id, 
          profile: currentProfile?.role,
          email: currentProfile?.email
        });
        
        // Special case for admin@aicrm.com
        if (email === 'admin@aicrm.com' && (!currentProfile || currentProfile.role !== 'admin')) {
          console.log('useAuth: Fixing admin role for admin@aicrm.com');
          try {
            await authService.fixUserRole(email, 'admin');
            // Fetch updated profile
            const { profile: updatedProfile } = await authService.getCurrentUser();
            setUser(currentUser);
            setProfile(updatedProfile);
          } catch (fixError) {
            console.error('useAuth: Error fixing admin role:', fixError);
          }
        } else {
          setUser(currentUser);
          setProfile(currentProfile);
        }
        
        setLoading(false);
        return { 
          user: { 
            ...currentUser, 
            ...currentProfile,
            // Ensure role is set correctly
            role: email === 'admin@aicrm.com' ? 'admin' : (currentProfile?.role || 'client')
          }, 
          error: null 
        };
      }
      
      setLoading(false);
      return { user: null, error: new Error('Sign in failed') };
    } catch (error: any) {
      console.error('useAuth: Sign in error:', error);
      setLoading(false);
      return { user: null, error };
    }
  };

  const signUp = async (email: string, password: string, userData: { full_name: string; role: 'admin' | 'client' }) => {
    setLoading(true);
    try {
      console.log('useAuth: Signing up user:', email, userData);
      console.log('useAuth: User role during signup:', userData.role);
      
      // Special case for admin@aicrm.com
      if (email === 'admin@aicrm.com') {
        userData.role = 'admin';
      }
      
      const { data, error } = await authService.signUp(email, password, userData);
      
      if (error) {
        console.error('useAuth: Sign up error:', error);
        setLoading(false);
        return { user: null, error };
      }

      if (data.user) {
        console.log('useAuth: Sign up success:', data.user.id);
        setLoading(false);
        // For signup, we don't automatically set the user state
        // They need to sign in after account creation
        return { user: data.user, error: null };
      }
      
      setLoading(false);
      return { user: null, error: new Error('Sign up failed') };
    } catch (error: any) {
      console.error('useAuth: Sign up error:', error);
      setLoading(false);
      return { user: null, error };
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      console.log('useAuth: Signing out');
      await authService.signOut();
      setUser(null);
      setProfile(null);
      setLoading(false);
    } catch (error) {
      console.error('useAuth: Error signing out:', error);
      setLoading(false);
    }
  };

  // Combine user and profile data
  const combinedUser = user && profile ? {
    ...user,
    ...profile,
    // Special case for admin@aicrm.com
    role: user.email === 'admin@aicrm.com' ? 'admin' : (profile.role || 'client')
  } : null;
  
  console.log('useAuth: Current state', { 
    userId: combinedUser?.id, 
    email: combinedUser?.email,
    role: combinedUser?.role, 
    loading 
  });

  return {
    user: combinedUser,
    loading,
    signIn,
    signUp,
    signOut,
  };
};